import React from 'react';
import type { AssistantToolCall } from 'components/framework/models';
import Agent, {
  type AgentInstances,
  type AgentParams,
  type AgentSuggestions,
} from '../Agent';
import { contentToEditorContent, contentToHtml } from '../utils/contentToHtml';
import { jsonFetch } from 'javascripts/fetch';
import { showNoticeMessage } from 'javascripts/messages';

class DiscoveryResearchAgent extends Agent {
  static name = 'Discovery::ResearchAgent';

  static publicName = 'Discovery';

  static available(
    agentParams: AgentParams,
    agentInstances: AgentInstances
  ): boolean {
    if (window.currentAccount?.hasGeneralAiAssistant) {
      // Check if the agent is explicitly enabled by the controller otherwise its disabled by default
      if ((window.availableAgents || []).includes('Discovery::ResearchAgent')) {
        return true;
      }
    }
    return false;
  }

  getSuggestions(): AgentSuggestions {
    return [
      {
        title: 'Summarize',
        prompt:
          'Tell me what you are distilling and how long you want it to be after it is shortened. You can refer to existing Aha! studies and interviews by their ID.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to summarize text.\n\nBefore starting though determine if I have told you how I would like you to summarize the text. If not, ask.
Let's start...\n\nThe text is: `,
        icon: 'summarize',
      },
      {
        title: 'Uncover',
        prompt: 'Tell me what types of information you would like to reveal.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to uncover insights from text.\n\nBefore starting though determine if I have told you the types of insights I would like you to reveal. If not, ask.
Let's start...\n\nThe text is: `,
        icon: 'whiteboard',
      },
      {
        title: 'Coalesce',
        prompt:
          'Tell me what concepts you want to group to understand the trends.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to coalesce information to identify trends from text.\n\nBefore starting though determine if I have told you how I want the information grouped. If not, ask.
Let's start...\n\nThe text is: `,
        icon: 'list',
      },
      {
        title: 'Join',
        prompt:
          'Tell me what information you want to combine with data from interviews or studies.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to join additional information to completed interviews or studies.\n\nBefore starting though determine if I have told you how I want the information joined. If not, ask.
Let's start...\n\nThe interviews and studies along with the additional information is: `,
        icon: 'draft',
      },
      {
        title: 'Validate',
        prompt:
          'Tell me what assumptions you want to test to determine if they are true.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to test assumptions to help validate if they are true.\n\nBefore starting though determine if I have provided clear assumptions. If not, ask.
Let's start...\n\nThe assumptions are: `,
        icon: 'refine',
      },
    ];
  }

  getToolActions(toolCall: AssistantToolCall): React.ReactNode {
    if (!this.agentParams.insightable) {
      return null;
    }

    if (
      toolCall.status === 'complete' &&
      toolCall.content?.panel?.body &&
      ['text/plan', 'text/markdown'].includes(
        toolCall.content.panel.contentType
      )
    ) {
      return (
        <aha-menu>
          <aha-button kind='primary' slot='control' size='small'>
            Insert <i className='fa-regular fa-chevron-down' />
          </aha-button>
          <aha-menu-content>
            {this.agentParams.existingInsightRows?.length > 0 && (
              <>
                {this.agentParams.existingInsightRows.map(row => (
                  <aha-menu-item
                    key={row.id}
                    onClick={() =>
                      this.insertInsightRow(
                        row.id,
                        row.name,
                        toolCall.content?.preview?.title ||
                          toolCall.content?.panel?.title,
                        toolCall.content?.panel?.body,
                        toolCall.content?.panel?.contentType
                      )
                    }
                  >
                    Add to {row.name}
                  </aha-menu-item>
                ))}
              </>
            )}
            <aha-menu-item
              onClick={() =>
                this.insertInsightRow(
                  undefined,
                  undefined,
                  toolCall.content?.preview?.title ||
                    toolCall.content?.panel?.title,
                  toolCall.content?.panel?.body,
                  toolCall.content?.panel?.contentType
                )
              }
            >
              Add as new insight
            </aha-menu-item>
          </aha-menu-content>
        </aha-menu>
      );
    }
  }

  insertInsightRow(
    existingInsightRowId: string | undefined,
    existingInsightRowName: string | undefined,
    name: string | undefined,
    content: string | undefined,
    contentType: string | undefined
  ): void {
    if (!content) return;
    const editorContent = contentToEditorContent(
      contentToHtml(content, contentType)
    );
    const editorJSON = JSON.stringify({ children: editorContent });

    // Covnert name from a filename to a title remove any file extension and replace - or _ with a space
    if (name) {
      name = name
        .replace(/\.[^/.]+$/, '') // Remove file extension
        .replace(/[-_]/g, ' '); // Replace - or _ with a space
    }

    let params: {
      discovery_insight_row: {
        name?: string;
        insight_type?: 'ai_artifact';
        description_attributes: {
          editor_content?: string;
          append_editor_content?: string;
        };
      };
      discovery_interview_id?: string;
      discovery_study_id?: string;
    };
    let url: string;
    let method: 'POST' | 'PATCH';

    if (existingInsightRowId) {
      url = `/discovery_insight_rows/${existingInsightRowId}`;
      method = 'PATCH';
      params = {
        discovery_insight_row: {
          description_attributes: {
            append_editor_content: editorJSON,
          },
        },
      };
    } else {
      url = `/discovery_insight_rows`;
      method = 'POST';
      params = {
        discovery_insight_row: {
          name,
          insight_type: 'ai_artifact',
          description_attributes: {
            editor_content: editorJSON,
          },
        },
      };
    }

    if (this.agentParams.insightable === 'Interview') {
      params.discovery_interview_id = this.agentParams.interviewId;
    } else if (this.agentParams.insightable === 'Study') {
      params.discovery_study_id = this.agentParams.studyId;
    }

    jsonFetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(params),
    })
      .then(response => {
        if (response.ok) {
          if (existingInsightRowId) {
            showNoticeMessage(
              `Added artifact to ${existingInsightRowName} on ${this.agentParams.insightable.toLowerCase()} insight report.`
            );
          } else {
            showNoticeMessage(
              `Added artifact to the ${this.agentParams.insightable.toLowerCase()} insight report.`
            );
          }
        } else {
          console.error('Error adding insight row:', response.statusText);
        }
      })
      .catch(error => {
        console.error('Error adding insight row:', error);
      });
  }
}
Agent.register(DiscoveryResearchAgent);
